import { useMemo, useState } from 'react';
import { Card, Badge, Modal } from '@azyntor-labs/nexus-ui';
import { Camera, ShieldAlert, Video, Monitor, Image as ImageIcon } from 'lucide-react';
import type {
  AttemptFullDetailSnapshot,
  AttemptFullDetailViolation,
} from '@/api/endpoints';

interface Props {
  snapshots: AttemptFullDetailSnapshot[];
  violations: AttemptFullDetailViolation[];
  startTime?: string;
  endTime?: string;
}

const severityTone: Record<string, 'danger' | 'warning' | 'info' | 'secondary'> = {
  critical: 'danger',
  high: 'danger',
  medium: 'warning',
  low: 'info',
};

function fmtTime(iso?: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function ProctoringEvidence({ snapshots, violations, startTime, endTime }: Props) {
  const [zoomed, setZoomed] = useState<AttemptFullDetailSnapshot | null>(null);

  // Split snapshots by kind for rendering.
  const faceSnaps = snapshots.filter((s) => s.kind === 'face');
  const violationSnaps = snapshots.filter((s) => s.kind === 'violation_snapshot');
  const webcamRec = snapshots.find((s) => s.kind === 'webcam_recording');
  const screenRec = snapshots.find((s) => s.kind === 'screen_recording');

  // Build timeline data — x position in % of exam duration.
  const timelineItems = useMemo(() => {
    if (!startTime || !endTime) return [];
    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();
    const span = end - start;
    if (span <= 0) return [];
    return violations
      .map((v) => {
        const t = new Date(v.timestamp).getTime();
        if (Number.isNaN(t) || t < start || t > end) return null;
        return {
          ...v,
          xPct: ((t - start) / span) * 100,
        };
      })
      .filter(Boolean) as (AttemptFullDetailViolation & { xPct: number })[];
  }, [violations, startTime, endTime]);

  // Violation type counts
  const byType = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const v of violations) counts[v.type] = (counts[v.type] || 0) + 1;
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [violations]);

  return (
    <div className="space-y-4">
      {/* Violation timeline */}
      <Card>
        <div className="p-5 border-b border-white/5">
          <h2 className="text-lg font-semibold text-white">Proctoring evidence</h2>
          <p className="text-xs text-white/40 mt-1">
            {violations.length} violation{violations.length === 1 ? '' : 's'} recorded
          </p>
        </div>

        <div className="p-5 space-y-4">
          {/* Type summary chips */}
          {byType.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {byType.map(([type, count]) => (
                <Badge key={type} variant="secondary" size="sm">
                  {type.replace(/_/g, ' ')} × {count}
                </Badge>
              ))}
            </div>
          )}

          {/* Timeline bar */}
          {timelineItems.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-white/50">
                Timeline ({fmtTime(startTime)} → {fmtTime(endTime)})
              </div>
              <div className="relative h-8 rounded bg-white/[0.04] border border-white/[0.06]">
                {timelineItems.map((v, i) => {
                  const tone = severityTone[v.severity.toLowerCase()] || 'secondary';
                  const color =
                    tone === 'danger'
                      ? 'bg-red-400'
                      : tone === 'warning'
                      ? 'bg-amber-400'
                      : tone === 'info'
                      ? 'bg-sky-400'
                      : 'bg-white/40';
                  return (
                    <div
                      key={i}
                      className={`absolute top-1 bottom-1 w-0.5 ${color}`}
                      style={{ left: `${v.xPct}%` }}
                      title={`${v.type} · ${v.severity || 'n/a'} · ${fmtTime(v.timestamp)}`}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Violation list */}
          {violations.length === 0 && (
            <p className="text-sm text-white/40 italic">No proctoring violations recorded.</p>
          )}
          {violations.length > 0 && (
            <div className="max-h-72 overflow-y-auto rounded border border-white/[0.06] divide-y divide-white/[0.04]">
              {violations.map((v, i) => {
                const tone = severityTone[v.severity?.toLowerCase()] || 'secondary';
                return (
                  <div key={i} className="p-3 flex items-start gap-3 text-sm">
                    <ShieldAlert className="w-4 h-4 text-amber-400/80 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-white">{v.type.replace(/_/g, ' ')}</span>
                        {v.severity && <Badge variant={tone} size="sm">{v.severity}</Badge>}
                        <span className="text-xs text-white/40 ml-auto">{fmtTime(v.timestamp)}</span>
                      </div>
                      {v.description && (
                        <p className="text-xs text-white/60 mt-0.5">{v.description}</p>
                      )}
                      {v.screenshot_url && (
                        <button
                          onClick={() => setZoomed({ url: v.screenshot_url!, timestamp: v.timestamp, kind: 'screenshot', note: v.type })}
                          className="mt-1 text-xs text-teal-300 hover:text-teal-200 inline-flex items-center gap-1"
                        >
                          <ImageIcon className="w-3 h-3" /> View screenshot
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {/* Face snapshots gallery */}
      {faceSnaps.length > 0 && (
        <Card>
          <div className="p-5 border-b border-white/5">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <Camera className="w-4 h-4" /> Face snapshots ({faceSnaps.length})
            </h3>
          </div>
          <div className="p-5 grid grid-cols-3 md:grid-cols-6 gap-2">
            {faceSnaps.map((s, i) => (
              <button
                key={i}
                onClick={() => setZoomed(s)}
                className="aspect-square rounded overflow-hidden border border-white/10 hover:border-teal-400/60 transition group relative"
              >
                <img
                  src={s.url}
                  alt="face snapshot"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
                <div className="absolute inset-x-0 bottom-0 bg-black/60 text-[10px] text-white/70 px-1 py-0.5 opacity-0 group-hover:opacity-100 transition">
                  {fmtTime(s.timestamp)}
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Violation snapshots gallery */}
      {violationSnaps.length > 0 && (
        <Card>
          <div className="p-5 border-b border-white/5">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" /> Violation screenshots ({violationSnaps.length})
            </h3>
          </div>
          <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-2">
            {violationSnaps.map((s, i) => (
              <button
                key={i}
                onClick={() => setZoomed(s)}
                className="aspect-video rounded overflow-hidden border border-white/10 hover:border-red-400/50 transition group relative"
              >
                <img src={s.url} alt={s.note} className="w-full h-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 bg-black/60 text-[10px] text-red-300 px-1 py-0.5">
                  {s.note}
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Recordings */}
      {(webcamRec || screenRec) && (
        <Card>
          <div className="p-5 border-b border-white/5">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <Video className="w-4 h-4" /> Recordings
            </h3>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {webcamRec && (
              <div>
                <div className="text-xs text-white/50 mb-2 inline-flex items-center gap-1">
                  <Camera className="w-3 h-3" /> Webcam
                </div>
                <video src={webcamRec.url} controls className="w-full rounded border border-white/10" />
              </div>
            )}
            {screenRec && (
              <div>
                <div className="text-xs text-white/50 mb-2 inline-flex items-center gap-1">
                  <Monitor className="w-3 h-3" /> Screen
                </div>
                <video src={screenRec.url} controls className="w-full rounded border border-white/10" />
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Zoom modal */}
      <Modal
        isOpen={!!zoomed}
        onClose={() => setZoomed(null)}
        title={zoomed?.note || 'Snapshot'}
        subtitle={zoomed ? fmtTime(zoomed.timestamp) : ''}
        size="lg"
      >
        {zoomed && (
          <div className="flex items-center justify-center">
            <img src={zoomed.url} alt="" className="max-h-[70vh] rounded" />
          </div>
        )}
      </Modal>
    </div>
  );
}
